import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { Check } from "lucide-react";
import { equipment, type ApplicationEquipmentStatus } from "@/features/equipment/api/equipment.api";
import { cn } from "@/lib/utils";
import { useEquipmentVisible } from "@/features/equipment/components/useEquipmentVisible";

const HIDDEN_STATUSES = new Set(["Draft", "Saved", "Declined", "Cancelled", "Merged"]);

function Box({ done }: { done: boolean }) {
  return (
    <span
      className={cn(
        "grid h-4 w-4 shrink-0 place-items-center rounded-full",
        done ? "bg-emerald-600 text-white" : "border-2 border-dashed border-slate-300",
      )}
    >
      {done && <Check className="h-2.5 w-2.5" strokeWidth={3} />}
    </span>
  );
}

export function EquipmentChecklist({
  applicationId,
  status,
  dbaName,
}: {
  applicationId: string;
  status: string;
  dbaName: string | null;
}) {
  const [state, setState] = useState<ApplicationEquipmentStatus | null>(null);
  const visible = useEquipmentVisible();

  useEffect(() => {
    if (!visible) return;
    let cancelled = false;
    equipment
      .applicationStatus([applicationId])
      .then((rows) => !cancelled && setState(rows[0] ?? null))
      .catch(() => !cancelled && setState(null));
    return () => {
      cancelled = true;
    };
  }, [applicationId, visible]);

  if (!visible || !state) return null;
  if (state.openOrderOnClosedApplication && state.orderId)
    return (
      <span className="inline-flex items-center gap-1.5 font-medium text-red-700">
        ⚠ Open equipment order on a closed application
        <Link to={`/equipment/orders/${state.orderId}`} className="text-blue-700 hover:underline">
          View
        </Link>
      </span>
    );
  if (HIDDEN_STATUSES.has(status) || (!state.done && !state.tracked)) return null;
  const name = dbaName || "this merchant";
  const to = state.done && state.orderId
    ? `/equipment/orders/${state.orderId}`
    : state.draftOrderId ? `/equipment/orders/${state.draftOrderId}` : `/equipment/new?applicationId=${applicationId}`;

  return (
    <span className="inline-flex items-center gap-1.5 text-slate-600" title={state.done ? `${state.number}${state.summary ? ` · ${state.summary}` : ""}` : `Equipment for ${name}`}>
      <Box done={state.done} />
      {state.done ? "Equipment ordered" : "Equipment not ordered"}
      <Link to={to} className="font-medium text-blue-700 hover:underline">
        {state.done ? "View" : state.draftOrderId ? "Continue →" : "Order →"}
      </Link>
    </span>
  );
}
